/**
 * Google 네이티브 로그인 — 플랫폼 분기.
 *
 * Android: 직접 작성한 in-app Capacitor 플러그인 GoogleLoginPlugin (name="GoogleLogin").
 *          android/app/src/main/java/kr/duckflex/peeka/app/socialauth/GoogleLoginPlugin.kt
 *          - requestIdToken 에 flavor 별 strings.xml `server_client_id`
 *            (= gradle.properties googleServerClientId{Local,Dev,Prod}) 를 사용 →
 *            환경별 GCP 프로젝트의 idToken aud 가 그 환경 BE 검증 audience 와 자동 일치.
 *          - 매번 계정 picker (아래 ⚠ 참조).
 *
 * ⚠ **로그인할 때마다 계정을 고르게 한다**(2026-09-15 사용자 결정). 캐시된 계정으로 조용히
 *   통과시키면 기기에 계정이 여럿일 때 **어느 계정으로 들어가는지 고를 수 없다** — peeka 는
 *   provider+계정 하나가 회원 하나라, 계정을 잘못 고르면 컬렉션이 빈 다른 회원이 된다.
 *   그래서 두 플랫폼 모두 signIn 전에 signOut 으로 캐시를 끊는다(웹은 authorize URL 의
 *   `prompt=select_account` 가 같은 일을 한다 — `lib/socialAuth.ts`).
 * iOS:     직접 플러그인(Swift) 미구현 → @codetrix-studio/capacitor-google-auth 사용.
 *          capacitor.config.ts 의 plugins.GoogleAuth.iosClientId (Bundle ID 별 발급) 필요.
 *          codetrix 는 iOS 에서 명시적 initialize() 호출이 필요하다 (Android 와 달리 auto-load 없음).
 *          init 누락 시 self.googleSignIn 이 nil 인 상태로 signIn() → force-unwrap 크래시.
 *
 * ⚠ 이전에는 Android 도 codetrix 를 썼는데, codetrix Android 는 requestIdToken 에
 *   capacitor.config.ts 의 clientId(= 모든 환경 공유 하드코딩값) 를 사용한다. 그래서 prod 빌드의
 *   idToken aud 가 prod BE audience(SSM google-client-id) 와 불일치 → BE 401 "유효하지 않은
 *   Google 토큰" 이 되는 문제가 있었다. Android 를 직접 플러그인으로 되돌려 flavor 별
 *   server_client_id 를 쓰게 해 환경별 자동 일치시킨다. iOS 는 flavor 개념이 없어 codetrix 유지.
 *
 * Android 는 직접 작성한 in-app Capacitor plugin(GoogleLoginPlugin) 을 쓴다.
 *
 * BE 와의 contract: 반환된 idToken 을 `/identity/verify/start/google` 의 idToken 필드로 전달.
 */
import { registerPlugin } from "@capacitor/core";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
import { isNative, nativeWarn, getPlatform } from "../index";

interface GoogleLoginResult {
  idToken: string;
  email?: string;
  displayName?: string;
}

interface GoogleLoginPluginApi {
  login(): Promise<GoogleLoginResult>;
  logout(): Promise<void>;
}

/**
 * Android 전용 in-app 플러그인 (MainActivity 가 registerPlugin(GoogleLoginPlugin.class)).
 * iOS 에는 이 native 구현이 없으므로 iOS 에서는 호출하지 않는다 (codetrix 로 분기).
 */
const GoogleLogin = registerPlugin<GoogleLoginPluginApi>("GoogleLogin");

// codetrix(iOS 전용) 는 명시적 1회 initialize() 필요.
let iosInitPromise: Promise<void> | null = null;

function ensureIosInitialized(): Promise<void> {
  if (!iosInitPromise) {
    iosInitPromise = GoogleAuth.initialize();
  }
  return iosInitPromise;
}

export async function nativeGoogleLogin(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    if (getPlatform() === "ios") {
      await ensureIosInitialized();
      // 캐시 계정으로 조용히 통과하지 않게 먼저 끊는다(위 ⚠). 로그인한 적이 없어도 무해하다.
      await GoogleAuth.signOut().catch(() => undefined);
      const user = await GoogleAuth.signIn();
      return user.authentication.idToken;
    }
    // Android — 직접 플러그인 (flavor 별 server_client_id 로 idToken 발급).
    const res = await GoogleLogin.login();
    return res.idToken || null;
  } catch (err) {
    nativeWarn("google-login", err);
    throw err;
  }
}

export async function nativeGoogleLogout(): Promise<void> {
  if (!isNative()) return;
  try {
    if (getPlatform() === "ios") {
      await ensureIosInitialized();
      await GoogleAuth.signOut();
      return;
    }
    await GoogleLogin.logout();
  } catch (err) {
    nativeWarn("google-logout", err);
  }
}
